import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router'

import { usePlatformAdmin } from './platform'
import { useSession } from './session'

import { readPendingInvite } from '@/features/invites/pending'
import { resellerApi, useResellerMe } from '@/features/reseller/api'
import { useMyWorkspaces } from '@/features/workspaces/queries'


function FullPageMessage({ children }: { children: ReactNode }) {
  return (
    <main
      style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}
      aria-live="polite"
    >
      <p style={{ color: 'var(--color-text-secondary)' }}>{children}</p>
    </main>
  )
}

/** Requires a session. Preserves where the user was going. */
export function RequireAuth() {
  const { status } = useSession()
  const location = useLocation()

  if (status === 'loading') return <FullPageMessage>Loading…</FullPageMessage>

  if (status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/auth/signin?next=${next}`} replace />
  }

  return <Outlet />
}

/**
 * Requires membership of the workspace in the URL.
 *
 * A non-member is sent to the picker rather than shown a 403 — and the API
 * returns 404 for the same request, so nothing confirms the workspace exists.
 */
export function RequireWorkspace() {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>()
  const { data: workspaces, isLoading, isFetching, isError } = useMyWorkspaces()

  if (isLoading) return <FullPageMessage>Loading workspace…</FullPageMessage>
  if (isError) return <FullPageMessage>Couldn’t load your workspaces.</FullPageMessage>

  // `isLoading` is false for a query that has cached data and is refetching, so
  // it alone cannot answer "does this user have no workspaces, or have we just
  // not heard back yet?". Redirecting on the first reading sent anyone who had
  // just created their first workspace back to onboarding.
  if (!workspaces?.length) {
    if (isFetching) return <FullPageMessage>Loading workspace…</FullPageMessage>
    return <Navigate to="/onboarding" replace />
  }

  const match = workspaces.find((w) => w.slug === workspaceSlug)
  if (!match) return <Navigate to="/workspaces" replace />

  return <Outlet />
}

/**
 * Route entry: send the user somewhere sensible based on what they have.
 *
 *   an invitation opened before sign-up → back to it
 *   a reseller login     → the reseller panel
 *   no workspaces, platform admin → the Super Admin panel
 *   no workspaces        → onboarding
 *   exactly one          → straight into it
 *   several              → the picker
 */
export function RootRedirect() {
  const { data: workspaces, isLoading, isError } = useMyWorkspaces()
  const reseller = useResellerMe()
  const pendingInvite = readPendingInvite()
  // Only asked when it decides anything: a platform admin with no workspace
  // of their own used to be sent to onboarding to create one.
  const noWorkspaces = !isLoading && !isError && !workspaces?.length
  const platformAdmin = usePlatformAdmin(noWorkspaces)

  // Someone who signed up as a reseller has no reseller record until their
  // first sign-in (the sign-up itself has no session). File the application
  // now, once, from what they entered.
  const { user } = useSession()
  const meta = user?.user_metadata as Record<string, unknown> | undefined
  const wantsReseller = meta?.['account_type'] === 'reseller'
  const [applyFailed, setApplyFailed] = useState(false)
  const needsApplication = wantsReseller && reseller.isError && !applyFailed
  const applying = useRef(false)
  useEffect(() => {
    if (!needsApplication || applying.current) return
    applying.current = true
    const name = typeof meta?.['business_name'] === 'string' ? meta['business_name'] : ''
    const phone = typeof meta?.['phone'] === 'string' ? meta['phone'] : ''
    void resellerApi
      .apply({ business_name: name || (user?.email ?? 'My business'), ...(phone ? { phone } : {}) })
      .then(() => reseller.refetch())
      // Give up quietly rather than leave the screen waiting forever.
      .catch(() => setApplyFailed(true))
  }, [needsApplication]) // eslint-disable-line react-hooks/exhaustive-deps

  if (pendingInvite) return <Navigate to={`/invite/${pendingInvite}`} replace />
  if (isLoading || reseller.isLoading) return <FullPageMessage>Loading…</FullPageMessage>
  // An unreachable API is not "no workspaces": sending that to onboarding
  // made a server outage look like a brand-new account.
  if (isError) {
    return <FullPageMessage>Couldn’t reach the server. Please try again in a moment.</FullPageMessage>
  }
  if (needsApplication) return <FullPageMessage>Sending your reseller application…</FullPageMessage>
  if (reseller.data) return <Navigate to="/reseller" replace />
  if (!workspaces?.length) {
    if (platformAdmin.isPending) return <FullPageMessage>Loading…</FullPageMessage>
    if (platformAdmin.isSuccess) return <Navigate to="/admin" replace />
    return <Navigate to="/onboarding" replace />
  }
  if (workspaces.length === 1) return <Navigate to={`/w/${workspaces[0]!.slug}`} replace />
  return <Navigate to="/workspaces" replace />
}
